import {
  Alert,
  BatchCompleteQuest,
  BatchPaymentsUpload,
  CreateCardDeckData,
  CreateCardDeckServerData,
  CreateCardDeckTraitData,
  CreateCardDeckVariantData,
  CreateProductParams,
  CreateTournamentParams,
  Device,
  Game,
  GameCredentials,
  InscriptionData,
  Notification,
  Quest,
  QuestOwner,
  RelevantAction,
  Role,
  RolePermission,
  RouletteValue,
  TreasuresGame,
  UpdateProductParams,
  UpdateTournamentData,
  User,
  UserContent,
  ProductsCodesBatchUpload,
  ApiEvent,
  AdminLevelReward
} from '@olagg/db-types'
import { CreateTokenData, UpdateTokenData } from '@olagg/web3/dist/domain/types/token.type'
import { Endpoint, Method } from "./constants";

import { IUseAlerts } from "../interfaces/IUseAlerts";
import { IUseAssignments } from "../interfaces/IUseAssignments";
import { IUseCredentials } from "../interfaces/IUseCredentials";
import { IUseInscriptions } from "../interfaces/IUseInscriptions";
import { IUseItems } from "../interfaces/IUseItems";
import { IUseQuests } from '../interfaces/IUseQuests'
import { IUseQuestsOwnerId } from '../interfaces/IUseQuestsOwnerId'
import { IUseRelevantActions } from "../interfaces/IUseRelevantActions";
import { IUseTokens } from "../interfaces/IUseTokens";
import { IUseTournaments } from "../interfaces/IUseTournaments";
import { IUseTreasuresGame } from "../interfaces/IUseTreasuresGame";
import { IUseUserContent } from "../interfaces/IUserUserContent";
import { IUseUsers } from '../interfaces/IUseUsers'
import { IUseProducts } from '../interfaces/IUseProducts';
import { IUseOrders } from '../interfaces/IUseOrders';
import path from 'path';
import { create } from 'domain';

const parameterize = (params: object) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value) {
      return `${acc}&${key}=${value}`
    }
    return acc
  }, '')
}

export const AdminEventsEndpoints = {
  all: ({ title, limit, page, active }: { title?: string, limit: number; page: number, active?: boolean }) => ({
    method: Method.GET,
    path: `/admin/events?limit=${limit}&page=${page}${title ? `&title=${title}` : ''}${active !== undefined ? `&active=${active}` : ''}`
  }),
  show: (id: string) => ({
    method: Method.GET,
    path: `/admin/events/${id}`
  }),
  create: (data: Partial<ApiEvent>, rescheduled?: boolean) => ({
    method: Method.POST,
    path: '/admin/events',
    body: {
      event: data,
      ...(rescheduled !== undefined && { rescheduled })
    },
  }),
  update: (id: string, data: Partial<ApiEvent>, rescheduled?: boolean) => ({
    method: Method.PUT,
    path: `/admin/events/${id}`,
    body: {
      event: data,
      ...(rescheduled !== undefined && { rescheduled })
    },
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/events/${id}`
  }),
}

export const AdminQuestEndpoints = {
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/quests/${id}`
  }),
  all: (filter: IUseQuests) => ({
    method: Method.GET,
    path: `/admin/quests?${parameterize(filter)}`
  }),
  ByOwnerId: ({ id }: IUseQuestsOwnerId) => ({
    method: Method.GET,
    path: `/quests?owner_id=${id}`
  }),
  create: (data: Partial<Quest>) =>
  ({
    method: Method.POST,
    path: '/admin/quests',
    body: { quest: data }
  } as Endpoint<Partial<Quest>>),
  update: (id: string, data: Partial<Quest>) =>
  ({
    method: Method.PUT,
    path: `/admin/quests/${id}`,
    body: data
  } as Endpoint<Partial<Quest>>),
  delete: (id: string) => ({
    method: Method.DELETE as Method.DELETE,
    path: `/admin/quests/${id}`
  }),
  batchComplete: (id: string, data: BatchCompleteQuest) =>
  ({
    method: Method.PUT,
    path: `/admin/quests/bulk_complete`,
    body: data,
    isForm: true
  } as Endpoint<Partial<Quest>>),
}

export const AdminGameEndpoints = {
  all: ({ page, limit, title }: { page: number, limit: number, title?: string }) => ({
    method: Method.GET,
    path: `/admin/games?page=${page}&limit=${limit}&title=${title ?? ''}`
  }),
  find: (slug: string) => ({
    method: Method.GET,
    path: `/admin/games/${slug}`
  }),
  create: (data: Partial<Game>) => ({
    method: Method.POST,
    path: '/admin/games',
    body: { game: data }
  } as Endpoint<Partial<Quest>>),
  update: (slug: string, data: Partial<Game>) => ({
    method: Method.PUT,
    path: `/admin/games/${slug}`,
    body: { game: data }
  } as Endpoint<Partial<Quest>>),
}

export const AdminOwnersEndpoints = {
  all: ({ name, page, limit }: { name?: string, page: number, limit: number }) => ({
    method: Method.GET,
    path: `/admin/owners?name=${name ?? ''}&page=${page}&limit=${limit}`
  }),
  find: (address: string) => ({
    method: Method.GET,
    path: `/admin/owners/${address}`
  }),
  create: (data: Partial<QuestOwner>) => ({
    method: Method.POST,
    path: '/admin/owners',
    body: { owner: data }
  } as Endpoint<Partial<QuestOwner>>),
  update: (address: string, data: Partial<QuestOwner>) => ({
    method: Method.PUT,
    path: `/admin/owners/${address}`,
    body: { owner: data }
  } as Endpoint<Partial<QuestOwner>>),
}

export const AdminNotificationEndpoints = {
  all: ({
    page,
    limit,
    title,
    userId
  }: {
    page: number,
    limit: number,
    title?: string,
    userId?: number,
  }) => ({
    method: Method.GET,
    path: `/admin/notifications?page=${page}&limit=${limit}&title=${title ?? ''}&userId=${userId ?? ''}`
  }),
  create: (data: Partial<Notification>) => ({
    method: Method.POST,
    path: '/admin/notifications',
    body: { notification: data }
  }),
  subscribe: (data: Partial<Device>) => ({
    method: Method.POST,
    path: '/admin/notifications/subscriptions',
    body: data
  }),
}

export const AdminPushNotificationEndpoints = {
  all: ({
    page,
    limit,
    title,
    status,
    segment_id,
    user_id,
    from_date,
    to_date,
    order_by,
    order_dir
  }: {
    page: number,
    limit: number,
    title?: string,
    status?: string,
    segment_id?: number,
    user_id?: number,
    from_date?: string,
    to_date?: string,
    order_by?: string,
    order_dir?: string,
  }) => ({
    method: Method.GET,
    path: `/admin/push_notifications?page=${page}&limit=${limit}&title=${title ?? ''}&status=${status ?? ''}&segment_id=${segment_id ?? ''}&user_id=${user_id ?? ''}&from_date=${from_date ?? ''}&to_date=${to_date ?? ''}&order_by=${order_by ?? ''}&order_dir=${order_dir ?? ''}`
  }),
  find: (uid: string) => ({
    method: Method.GET,
    path: `/admin/push_notifications/${uid}`
  }),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/push_notifications',
    body: { push_notification: data }
  }),
  update: (uid: string, data: any) => ({
    method: Method.PATCH,
    path: `/admin/push_notifications/${uid}`,
    body: { push_notification: data }
  }),
  delete: (uid: string) => ({
    method: Method.DELETE,
    path: `/admin/push_notifications/${uid}`
  }),
  send: (uid: string) => ({
    method: Method.POST,
    path: `/admin/push_notifications/${uid}/send`
  }),
  resend: (uid: string) => ({
    method: Method.POST,
    path: `/admin/push_notifications/${uid}/resend`
  }),
}

export const AdminSegmentEndpoints = {
  all: ({ page, limit, name }: { page: number, limit: number, name?: string }) => ({
    method: Method.GET,
    path: `/admin/segments?page=${page}&limit=${limit}&name=${name ?? ''}`
  }),
  find: (id: number) => ({
    method: Method.GET,
    path: `/admin/segments/${id}`
  }),
  create: (body: { name: string, description: string }) => ({
    method: Method.POST,
    path: `/admin/segments`,
    body: { segment: body }
  }),
  update: (id: number, body: { name: string, description: string }) => ({
    method: Method.PUT,
    path: `/admin/segments/${id}`,
    body: { segment: body }
  }),
  delete: (id: number) => ({
    method: Method.DELETE,
    path: `/admin/segments/${id}`
  }),
  assignUsers: (id: number, user_ids: number[]) => ({
    method: Method.POST,
    path: `/admin/segments/${id}/assign_users`,
    body: { user_ids }
  })
}

export const AdminUserEndpoints = {
  all: ({ page, limit, country, query, withWallets }: IUseUsers) => ({
    method: Method.GET,
    path: `/admin/users?page=${page}&limit=${limit}&country=${country ?? ''}&query=${query ?? ''}${withWallets ? '&withWallets=true' : ''}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/users/${id}`
  }),
  update: (id: string, data: Partial<User>) => ({
    method: Method.PUT,
    path: `/admin/users/${id}`,
    body: { user: data }
  }),
}

export const AdminUserContentEndpoints = {
  all: ({ page, limit, title, gameId }: IUseUserContent) => ({
    method: Method.GET,
    path: `/admin/user_contents?page=${page}&limit=${limit}&title=${title ?? ''}&gameId=${gameId ?? ''}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/user_contents/${id}`
  }),
  update: (id: string, data: Partial<UserContent>) => ({
    method: Method.PUT,
    path: `/admin/user_contents/${id}`,
    body: { userContent: data }
  }),
  approve: (id: string, data: Partial<UserContent>) => ({
    method: Method.PUT,
    path: `/admin/user_contents/${id}/approve`,
    body: { userContent: data }
  }),
  reject: (id: string, data: Partial<UserContent>) => ({
    method: Method.PUT,
    path: `/admin/user_contents/${id}/reject`,
    body: { userContent: data }
  }),
}

export const AdminMarketPlaceEndpoints = {
  all: ({ page, limit, title, type, name }: IUseItems) => ({
    method: Method.GET,
    path: `/admin/items?page=${page}&limit=${limit}&title=${title ?? ''}&name=${name ?? ''}&type=${type ?? ''}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/items/${id}`
  }),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/items',
    body: data,
    isForm: true,
  }),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/items/${id}`,
    body: data,
    isForm: true
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/items/${id}`
  }),
  stocks: (id: string) => ({
    method: Method.GET,
    path: `/admin/items/${id}/stocks`
  }),
  regions: () => ({
    method: Method.GET,
    path: `/admin/items/regions`,
  })
}

export const AdminCouponsEndpoints = {
  all: ({ name, status, page, limit }: { name?: string, status?: string, page: number, limit: number }) => ({
    method: Method.GET,
    path: `/admin/coupons?${parameterize({ name, status, page, limit })}`
  }),
  show: (id: string) => ({
    method: Method.GET,
    path: `/admin/coupons/${id}`
  }),
  showPromotional: (id: string) => ({
    method: Method.GET,
    path: `/admin/promotional_coupons/${id}`
  }),
  create: ({ userId, percentage, limit, blockchainTokenId, dueDate }: { dueDate: string, userId: string; percentage: number; limit: number, blockchainTokenId: string }) => ({
    method: Method.POST,
    path: `/admin/coupons`,
    body: { userId, percentage, limit, blockchainTokenId, dueDate, type: "PercentageCoupon" }
  }),
  createPromotional: ({ code, percentage, limit, maxUses, dueDate }: { dueDate: string, code: string; percentage: number; limit: number, maxUses: number }) => ({
    method: Method.POST,
    path: `/admin/promotional_coupons`,
    body: { code, percentage, limit, maxUses, dueDate, type: "PercentageCoupon" }
  }),
  updatePromotional: ({ id, code, percentage, limit, maxUses, dueDate }: { id: string, dueDate: string, code: string; percentage: number; limit: number, maxUses: number }) => ({
    method: Method.PUT,
    path: `/admin/promotional_coupons/${id}`,
    body: { code, percentage, limit, maxUses, dueDate }
  }),
  batchCreate: (productId: string, quantity: number) => ({
    method: Method.POST,
    path: `/admin/coupons`,
    body: { quantity, productId, type: "TotalCoupon" }
  }),
  export: (productId: string) => ({
    method: Method.GET,
    path: `/admin/coupons/export?productId=${productId}`,
    responseType: 'text/csv',
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/coupons/${id}`
  }),
  deletePromotional: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/promotional_coupons/${id}`
  }),
}

export const AdminProductsEndpoints = {
  allBonoxs: ({ name }: { name?: string }) => ({
    method: Method.GET,
    path: `/admin/bonoxs?name=${name ?? ''}`,
  }),
  allCategories: () => ({
    method: Method.GET,
    path: `/products/categories`,
  }),
  all: ({ page, limit, name }: IUseProducts) => ({
    method: Method.GET,
    path: `/admin/products?page=${page}&limit=${limit}&name=${name ?? ''}`,
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/products/${id}`,
  }),
  create: (data: CreateProductParams) => ({
    method: Method.POST,
    path: '/admin/products',
    body: { product: data },
  }),
  update: (id: string, data: UpdateProductParams) => ({
    method: Method.PUT,
    path: `/admin/products/${id}`,
    body: { product: data },
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/products/${id}`,
  })
}

export const AdminOrdersEndpoints = {
  all: ({ page, limit, order_by, user_name, status }: IUseOrders) => ({
    method: Method.GET,
    path: `/admin/orders?page=${page || 1}&limit=${limit || 10}&order_by=${order_by || ''}&user_name=${user_name || ''}&status=${status || ''}`
  }),
  refund: (id: string) => ({
    method: Method.POST,
    path: `/admin/orders/${id}/refund`
  }),
  retryBonox: (id: string) => ({
    method: Method.POST,
    path: `/admin/orders/${id}/retry_bonox`
  }),
  retryPayment: (id: string) => ({
    method: Method.POST,
    path: `/admin/orders/${id}/retry_payment`
  })
}

export const AdminTournamentsEndpoints = {
  all: ({ page, limit, title, game, sortByStartDate }: IUseTournaments) => ({
    method: Method.GET,
    path: `/admin/tournaments?page=${page}&limit=${limit}&title=${title ?? ''}&slug=${game ?? ''}&sortByStartDate=${sortByStartDate ?? 'asc'}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/tournaments/${id}`
  }),
  create: (data: CreateTournamentParams) => ({
    method: Method.POST,
    path: '/admin/tournaments',
    body: { tournament: data }
  }),
  update: (id: string, data: UpdateTournamentData) => ({
    method: Method.PUT,
    path: `/admin/tournaments/${id}`,
    body: { tournament: data }
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/tournaments/${id}`
  }),
  inscriptions: (filters: IUseInscriptions) => ({
    method: Method.GET,
    path: `/admin/tournaments/${filters.tournamentId}/inscriptions?${parameterize(filters)}`
  }),
  deleteInscription: (id: number, inscriptionId: string) => ({
    method: Method.DELETE,
    path: `/admin/tournaments/${id}/inscriptions/${inscriptionId}`
  }),
  addInscription: (id: string, data: Partial<InscriptionData>) => ({
    method: Method.POST,
    path: `/admin/tournaments/${id}/inscriptions`,
    body: { inscription: data },
  }),
  addInscriptionBatch: (id: string, data: FormData) => ({
    method: Method.POST,
    path: `/admin/tournaments/${id}/inscriptions/batch`,
    body: data,
    isForm: true
  }),
  organizers: () => ({
    method: Method.GET,
    path: `/admin/tournaments/organizers`
  }),
}

export const AdminTokensEndpoints = {
  all: ({ page, limit, name }: IUseTokens) => ({
    method: Method.GET,
    path: `/admin/tokens?page=${page}&limit=${limit}&name=${name ?? ''}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/tokens/${id}`
  }),
  create: (data: CreateTokenData) => ({
    method: Method.POST,
    path: '/admin/tokens',
    body: data
  }),
  update: (id: string, data: UpdateTokenData) => ({
    method: Method.PUT,
    path: `/admin/tokens/${id}`,
    body: { token: data }
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/tokens/${id}`
  }),
}

export const AdminTokenDropsEndpoints = {
  all: ({ page, limit, title }: { page: number, limit: number, title?: string }) => ({
    method: Method.GET,
    path: `/admin/token-drops?page=${page}&limit=${limit}&title=${title}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/token-drops/${id}`
  }),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/token-drops',
    body: data,
  }),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/token-drops/${id}`,
    body: data,
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/token-drops/${id}`
  }),
}

export const AdminAssignmentEndpoints = {
  all: ({ page, limit, userId, tokenId }: IUseAssignments) => ({
    method: Method.GET,
    path: `/admin/tokens/${tokenId}/assignments?page=${page}&limit=${limit}&userId=${userId ?? ''}`
  }),
  create: (tokenId: string, userId: number, name: string, image: string) => ({
    method: Method.POST,
    path: `/admin/tokens/${tokenId}/assignments`,
    body: {
      assignment: { userId, token: { name, image } },
    }
  }),
  createBatch: (tokenId: string, data: FormData) => ({
    method: Method.POST,
    path: `/admin/tokens/${tokenId}/assignments/batch`,
    body: data,
    isForm: true
  }),
  delete: (tokenId: string, id: string) => ({
    method: Method.DELETE,
    path: `/admin/tokens/${tokenId}/assignments/${id}`
  }),
}

export const AdminScholarshipEndpoints = {
  all: (params: URLSearchParams) => ({
    method: Method.GET,
    path: `/admin/scholarships?${params.toString()}`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/scholarships/${id}`
  })
}

export const AdminCredentialsEndpoints = {
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/credentials/${id}`
  }),
  all: ({ slug, status, query, page, limit }: IUseCredentials) => ({
    method: Method.GET,
    path: `/admin/credentials?${parameterize({ slug, status, query, page, limit })}`
  }),
  update: (id: string, data: Partial<GameCredentials>) => ({
    method: Method.PUT,
    path: `/admin/credentials/${id}`,
    body: { credential: data }
  }),
  create: (data: Partial<GameCredentials>) => ({
    method: Method.POST,
    path: `/admin/credentials`,
    body: { credential: data }
  }),
}

export const AdminRelevantActionsEndpoints = {
  all: ({ page, limit }: IUseRelevantActions) => ({
    method: Method.GET,
    path: `/admin/relevant_actions?${parameterize({ page, limit })}`
  }),
  update: (id: number, data: Partial<RelevantAction>) => ({
    method: Method.PUT,
    path: `/admin/relevant_actions/${id}`,
    body: { relevantAction: data }
  }),
  create: (data: Partial<RelevantAction>) => ({
    method: Method.POST,
    path: `/admin/relevant_actions`,
    body: { relevantAction: data }
  }),
  delete: (id: number) => ({
    method: Method.DELETE,
    path: `/admin/relevant_actions/${id}`,
  }),
  find: (id: number) => ({
    method: Method.GET,
    path: `/admin/relevant_actions/${id}`
  }),
}

export const AdminAlertsEndpoints = {
  all: ({ page, limit }: IUseAlerts) => ({
    method: Method.GET,
    path: `/admin/alerts?${parameterize({ page, limit })}`
  }),
  update: (id: number, data: Partial<Alert>) => ({
    method: Method.PUT,
    path: `/admin/alerts/${id}`,
    body: { alert: data }
  }),
  create: (data: Partial<Alert>) => ({
    method: Method.POST,
    path: `/admin/alerts`,
    body: { alert: data }
  }),
  delete: (id: number) => ({
    method: Method.DELETE,
    path: `/admin/alerts/${id}`,
  }),
  find: (id: number) => ({
    method: Method.GET,
    path: `/admin/alerts/${id}`
  }),
}

export const AdminRolesEndpoints = {
  roles: (filter: { page: number, limit: number }): Endpoint => ({
    method: Method.GET,
    path: `/admin/roles?${parameterize({ ...filter })}`
  }),
  createRole: (data: Partial<Role>): Endpoint => ({
    method: Method.POST,
    path: `/admin/roles`,
    body: { role: data }
  }),
  findRole: (id: number): Endpoint => ({
    method: Method.GET,
    path: `/admin/roles/${id}`,
  }),
  updateRole: (id: number, data: Partial<Role>): Endpoint => ({
    method: Method.PUT,
    path: `/admin/roles/${id}`,
    body: { role: data }
  }),
  deleteRole: (id: number): Endpoint => ({
    method: Method.DELETE,
    path: `/admin/roles/${id}`
  }),
  rolesPermissions: (filter: { page: number, limit: number }): Endpoint => ({
    method: Method.GET,
    path: `/admin/roles_permissions?${parameterize({ ...filter })}`
  }),
  createRolesPermission: (data: Partial<RolePermission>): Endpoint => ({
    method: Method.POST,
    path: `/admin/roles_permissions`,
    body: { permission: data }
  }),
  findRolesPermission: (id: number): Endpoint => ({
    method: Method.GET,
    path: `/admin/roles_permissions/${id}`,
  }),
  updateRolesPermission: (id: number, data: Partial<RolePermission>): Endpoint => ({
    method: Method.PUT,
    path: `/admin/roles_permissions/${id}`,
    body: { permission: data }
  }),
  deleteRolesPermission: (id: number): Endpoint => ({
    method: Method.DELETE,
    path: `/admin/roles_permissions/${id}`
  }),
}

export const AdminRouletteEndpoints = {
  all: (): Endpoint => ({
    method: Method.GET,
    path: `/admin/roulette_values`
  }),
  update: (id: number, data: Partial<RouletteValue>): Endpoint => ({
    method: Method.PUT,
    path: `/admin/roulette_values/${id}`,
    body: { roulette_value: data }
  }),
}

export type IPaymentsGetAllProps = {
  status?: string;
  userId: number;
  budget: string;
  page: number;
  limit: number;
};

export const AdminPaymentsEndpoints = {
  all: (filter: IPaymentsGetAllProps) => ({
    method: Method.GET,
    path: `/admin/payments?${parameterize(filter)}`
  }),
  export: () => ({
    method: Method.GET,
    path: `/admin/payments/export`
  }),
  new: (data: any) =>
  ({
    method: Method.POST,
    path: '/admin/payments',
    body: data
  } as Endpoint<any>),
  update: (id: any, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/payments/${id}`,
    body: data
  } as Endpoint<any>),
  bulk_create: (data: BatchPaymentsUpload) =>
  ({
    method: Method.POST,
    path: `/admin/payments/bulk_create`,
    body: data,
    isForm: true
  } as Endpoint<any>),
  bulk_update: (data: BatchPaymentsUpload) =>
  ({
    method: Method.PUT,
    path: `/admin/payments/bulk_update`,
    body: data,
    isForm: true
  } as Endpoint<any>),
  createPaymentsForTournamentWinners: (tournamentId: number, network: string) => ({
    method: Method.POST,
    path: `/admin/payments/create_payments_for_tournament_winners`,
    body: { tournamentId, network }
  }) as Endpoint<any>,
  resume: () => ({
    method: Method.GET,
    path: '/admin/payments/resume'
  }),
  bulk_update_after_airdrop: (data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/payments/bulk_update`,
    body: data,
    isForm: false
  } as Endpoint<any>),
}

export const AdminTreasuresGameEndpoints = {
  index: (params: IUseTreasuresGame): Endpoint => ({
    method: Method.GET,
    path: `/admin/treasures_games?${parameterize(params)}&include_beta_tested=true`
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/treasures_games/${id}`
  }),
  show: (slug: string): Endpoint => ({
    method: Method.GET,
    path: `/admin/treasures_games/${slug}`
  }),
  create: (data: Partial<TreasuresGame>) => ({
    method: Method.POST,
    path: '/admin/treasures_games',
    body: data,
  }),
  update: (id: string, data: Partial<TreasuresGame>) => ({
    method: Method.PUT,
    path: `/admin/treasures_games/${id}`,
    body: data,
  }),
  destroy: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/treasures_games/${id}`,
  }),
  movements: (id: string) => ({
    method: Method.GET,
    path: `/admin/treasures_games/${id}/treasures_games_movements`
  }),
  exportMovements: (id: string) => ({
    method: Method.GET,
    path: `/admin/treasures_games/${id}/treasures_games_movements/export`
  }),
}

export type IUseTreasureGameOpportunities = {
  status?: 'active' | 'expired' | 'used';
  page: number;
  limit: number;
};

export const AdminTreasureGameOpportunitiesEndpoints = {
  all: (filter: IUseTreasureGameOpportunities) => ({
    method: Method.GET,
    path: `/admin/treasure_opportunities?${parameterize(filter)}`
  }),
  show: (id: string) => ({
    method: Method.GET,
    path: `/admin/treasure_opportunities/${id}`
  }),
  create: (data: Partial<any>) => ({
    method: Method.POST,
    path: '/admin/treasure_opportunities',
    body: { treasure_opportunity: data }
  }),
  update: (id: string, data: Partial<any>) => ({
    method: Method.PUT,
    path: `/admin/treasure_opportunities/${id}`,
    body: { treasure_opportunity: data }
  }),
  destroy: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/treasure_opportunities/${id}`
  })
}

export const AdminSurveysEndpoints = {
  all: (filter: IPaymentsGetAllProps) => ({
    method: Method.GET,
    path: `/admin/surveys?${parameterize(filter)}`
  }),
  find: (id: any) =>
  ({
    method: Method.GET,
    path: `/admin/surveys/${id}`,
  } as Endpoint<any>),
  new: (data: any) =>
  ({
    method: Method.POST,
    path: '/admin/surveys',
    body: data
  } as Endpoint<any>),
  update: (id: any, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/surveys/${id}`,
    body: data
  } as Endpoint<any>)
}

export type IPreferencesGetAllProps = {
  name?: string;
  longName?: string;
  page: number;
  limit: number;
};

export type IStoriesGetAllProps = {
  title?: string;
  page: number;
  limit: number;
};

export const AdminUserPreferencesEndpoints = {
  gameCategories: {
    all: (filter: IPreferencesGetAllProps) => ({
      method: Method.GET,
      path: `/admin/game_categories?${parameterize(filter)}`
    }),
    find: (id: any) =>
    ({
      method: Method.GET,
      path: `/admin/game_categories/${id}`,
    } as Endpoint<any>),
    new: (data: any) =>
    ({
      method: Method.POST,
      path: '/admin/game_categories',
      body: data
    } as Endpoint<any>),
    update: (id: any, data: any) =>
    ({
      method: Method.PUT,
      path: `/admin/game_categories/${id}`,
      body: data
    } as Endpoint<any>),
    delete: (id: string) => ({
      method: Method.DELETE as Method.DELETE,
      path: `/admin/game_categories/${id}`
    }),
  },
  opportunities: {
    all: (filter: IPreferencesGetAllProps) => ({
      method: Method.GET,
      path: `/admin/opportunities?${parameterize(filter)}`
    }),
    find: (id: any) =>
    ({
      method: Method.GET,
      path: `/admin/opportunities/${id}`,
    } as Endpoint<any>),
    new: (data: any) =>
    ({
      method: Method.POST,
      path: '/admin/opportunities',
      body: data
    } as Endpoint<any>),
    update: (id: any, data: any) =>
    ({
      method: Method.PUT,
      path: `/admin/opportunities/${id}`,
      body: data
    } as Endpoint<any>),
    delete: (id: string) => ({
      method: Method.DELETE as Method.DELETE,
      path: `/admin/opportunities/${id}`
    }),
  },
  platforms: {
    all: (filter: IPreferencesGetAllProps) => ({
      method: Method.GET,
      path: `/admin/gaming_platforms?${parameterize(filter)}`
    }),
    find: (id: any) =>
    ({
      method: Method.GET,
      path: `/admin/gaming_platforms/${id}`,
    } as Endpoint<any>),
    new: (data: any) =>
    ({
      method: Method.POST,
      path: '/admin/gaming_platforms',
      body: data
    } as Endpoint<any>),
    update: (id: any, data: any) =>
    ({
      method: Method.PUT,
      path: `/admin/gaming_platforms/${id}`,
      body: data
    } as Endpoint<any>),
    delete: (id: string) => ({
      method: Method.DELETE as Method.DELETE,
      path: `/admin/gaming_platforms/${id}`
    }),
  }
}

export const AdminStoriesEndpoints = {
  all: (filter: IStoriesGetAllProps) => ({
    method: Method.GET,
    path: `/admin/stories?${parameterize(filter)}`,
  }),
  find: (id: any) =>
  ({
    method: Method.GET,
    path: `/admin/stories/${id}`,
  } as Endpoint<any>),
  create: (data: any) =>
  ({
    method: Method.POST,
    path: '/admin/stories',
    body: data
  } as Endpoint<any>),
  update: (id: any, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/stories/${id}`,
    body: data
  } as Endpoint<any>),
  delete: (id: any) =>
  ({
    method: Method.DELETE,
    path: `/admin/stories/${id}`,
  } as Endpoint<any>),
}

export type IStoryCategoriesGetAllProps = {
  name?: string;
  page: number;
  limit: number;
};

export type IBlockchainTokenCatalogGetAllProps = {
  name?: string;
  network?: string;
  tokenType?: string;
  page: number;
  limit: number;
}

export type IBlockchainNetworkGetAllProps = {
  name?: string;
  page: number;
  limit: number;
}

export const AdminStoryCategoriesEndpoints = {
  all: ({ name, limit, page }: IStoryCategoriesGetAllProps) => ({
    method: Method.GET,
    path: `/admin/story_categories?${parameterize({ name, limit, page })}`
  }),
  find: (id: number) =>
  ({
    method: Method.GET,
    path: `/admin/story_categories/${id}`
  } as Endpoint<any>),
  update: (id: number, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/story_categories/${id}`,
    body: data
  } as Endpoint<any>),
  create: (data: any) =>
  ({
    method: Method.POST,
    path: '/admin/story_categories',
    body: data
  } as Endpoint<any>),
  delete: (id: number) => ({
    method: Method.DELETE,
    path: `/admin/story_categories/${id}`
  }),
}

export const AdminBlockchainTokenCatalog = {
  all: ({ name, network, tokenType, limit, page }: IBlockchainTokenCatalogGetAllProps) => ({
    method: Method.GET,
    path: `/admin/blockchain_tokens?${parameterize({ name, network, tokenType, limit, page })}`
  }),
  find: (id: number) =>
  ({
    method: Method.GET,
    path: `/admin/blockchain_tokens/${id}`
  }),
  update: (id: number, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/blockchain_tokens/${id}`,
    body: data
  }),
  create: (data: any) =>
  ({
    method: Method.POST,
    path: `/admin/blockchain_tokens`,
    body: data
  }),
  delete: (id: any) =>
  ({
    method: Method.DELETE,
    path: `/admin/blockchain_tokens/${id}`
  })
}

export const AdminBlockchainNetwork = {
  all: ({ name, limit, page }: IBlockchainNetworkGetAllProps) => ({
    method: Method.GET,
    path: `/admin/blockchain_token_networks?${parameterize({ name, limit, page })}`
  }),
  find: (id: number) =>
  ({
    method: Method.GET,
    path: `/admin/blockchain_token_networks/${id}`
  }),
  update: (id: number, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/blockchain_token_networks/${id}`,
    body: data
  }),
  create: (data: any) =>
  ({
    method: Method.POST,
    path: `/admin/blockchain_token_networks`,
    body: data
  }),
  delete: (id: any) =>
  ({
    method: Method.DELETE,
    path: `/admin/blockchain_token_networks/${id}`
  })
}

export const AdminStreaksEndpoints = {
  all: () => ({
    method: Method.GET,
    path: `/admin/streak_campaigns`
  }),
  byId: (id: any) => ({
    method: Method.GET,
    path: `/admin/streak_campaigns/${id}`
  }),
  new: (data: any) =>
  ({
    method: Method.POST,
    path: '/admin/streak_campaigns',
    body: data
  } as Endpoint<any>),
  update: (id: any, data: any) =>
  ({
    method: Method.PUT,
    path: `/admin/streak_campaigns/${id}`,
    body: data
  } as Endpoint<any>),
}

export const AdminCardDecksEndpoints = {
  list: () => ({
    method: Method.GET,
    path: `/admin/card_decks`
  }),
  show: (deck_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}`
  }),
  create: (data: { card_deck: CreateCardDeckData }) => ({
    method: Method.POST,
    path: `/admin/card_decks`,
    body: data
  } as Endpoint<{ card_deck: CreateCardDeckData }>),
  update: (deck_id: string | number, data: { card_deck: Partial<CreateCardDeckData> }) => ({
    method: Method.PUT,
    path: `/admin/card_decks/${deck_id}`,
    body: data
  } as Endpoint<{ card_deck: Partial<CreateCardDeckData> }>),
  delete: (deck_id: string | number) => ({
    method: Method.DELETE,
    path: `/admin/card_decks/${deck_id}`
  })
}

export const AdminCardDecksServersEndpoints = {
  list: (deck_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}/discord_servers`
  }),
  show: (deck_id: string | number, server_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}/discord_servers/${server_id}`
  }),
  create: (deck_id: string | number, data: { discordServer: CreateCardDeckServerData }) => ({
    method: Method.POST,
    path: `/admin/card_decks/${deck_id}/discord_servers`,
    body: data
  } as Endpoint<{ discordServer: CreateCardDeckServerData }>),
  update: (deck_id: string | number, server_id: string | number, data: { discordServer: Partial<CreateCardDeckServerData> }) => ({
    method: Method.PUT,
    path: `/admin/card_decks/${deck_id}/discord_servers/${server_id}`,
    body: data
  } as Endpoint<{ discordServer: Partial<CreateCardDeckServerData> }>),
  delete: (deck_id: string | number, server_id: string | number) => ({
    method: Method.DELETE,
    path: `/admin/card_decks/${deck_id}/discord_servers/${server_id}`
  })
}

export const AdminCardDecksTraitsEndpoints = {
  list: (deck_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}/traits`
  }),
  show: (deck_id: string | number, trait_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}`
  }),
  create: (deck_id: string | number, data: { trait: CreateCardDeckTraitData }) => ({
    method: Method.POST,
    path: `/admin/card_decks/${deck_id}/traits`,
    body: data
  } as Endpoint<{ trait: CreateCardDeckTraitData }>),
  update: (deck_id: string | number, trait_id: string | number, data: { trait: Partial<CreateCardDeckTraitData> }) => ({
    method: Method.PUT,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}`,
    body: data
  } as Endpoint<{ trait: Partial<CreateCardDeckTraitData> }>),
  delete: (deck_id: string | number, trait_id: string | number) => ({
    method: Method.DELETE,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}`
  })
}

export const AdminCardDecksVariantsEndpoints = {
  list: (deck_id: string | number, trait_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}/variants`
  }),
  show: (deck_id: string | number, trait_id: string | number, variant_id: string | number) => ({
    method: Method.GET,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}/variants/${variant_id}`
  }),
  create: (deck_id: string | number, trait_id: string | number, data: { variant: CreateCardDeckVariantData }) => ({
    method: Method.POST,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}/variants`,
    body: data
  } as Endpoint<{ variant: CreateCardDeckVariantData }>),
  update: (deck_id: string | number, trait_id: string | number, variant_id: string | number, data: { variant: Partial<CreateCardDeckVariantData> }) => ({
    method: Method.PUT,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}/variants/${variant_id}`,
    body: data
  } as Endpoint<{ variant: Partial<CreateCardDeckVariantData> }>),
  delete: (deck_id: string | number, trait_id: string | number, variant_id: string | number) => ({
    method: Method.DELETE,
    path: `/admin/card_decks/${deck_id}/traits/${trait_id}/variants/${variant_id}`
  })
}


type IGetAllHostProps = {
  name?: string;
  page: number;
  limit: number;
};

export const HostEndpoints = {
  all: ({ name, page, limit }: IGetAllHostProps) => ({
    method: Method.GET,
    path: `/admin/hosts?page=${page}&limit=${limit}${name ? `&name=${name}` : ''}`
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/hosts/${id}`
  } as Endpoint<any>),
  create: (data: any) => ({
    method: Method.POST,
    path: `/admin/hosts`,
    body: data
  } as Endpoint<any>),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/hosts/${id}`,
    body: data
  } as Endpoint<any>),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/hosts/${id}`
  } as Endpoint<any>),
}

export type IUseSeasons = {
  page: number;
  limit: number;
  title?: string;
  visible?: boolean;
  status?: string;
}

export const AdminSeasonsEndpoints = {
  all: ({ page, limit, title, visible, status }: IUseSeasons) => {
    let query = `page=${page}&limit=${limit}`
    if (title) query += `&title=${encodeURIComponent(title)}`
    if (visible !== undefined) query += `&visible=${visible}`
    if (status) query += `&status=${status}`

    return {
      method: Method.GET,
      path: `/admin/game_seasons?${query}`
    } as Endpoint<any>
  },
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/game_seasons/${id}`
  } as Endpoint<any>),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/game_seasons',
    body: { game_season: data }
  } as Endpoint<any>),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/game_seasons/${id}`,
    body: { game_season: data }
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/game_seasons/${id}`
  } as Endpoint<any>),
  clone: (id: string) => ({
    method: Method.POST,
    path: `/admin/game_seasons/${id}/clone`
  } as Endpoint<any>)
}

export type IUseSeasonChallenges = {
  page: number;
  limit: number;
  title?: string;
  game_season_id?: number;
}

export const AdminSeasonChallengesEndpoints = {
  all: ({ page, limit, title, game_season_id }: IUseSeasonChallenges) => {
    let query = `page=${page}&limit=${limit}`
    if (title) query += `&title=${encodeURIComponent(title)}`
    if (game_season_id) query += `&game_season_id=${game_season_id}`

    return {
      method: Method.GET,
      path: `/admin/game_season_challenges?${query}`
    } as Endpoint<any>
  },
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/game_season_challenges/${id}`
  } as Endpoint<any>),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/game_season_challenges',
    body: { game_season_challenge: data }
  } as Endpoint<any>),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/game_season_challenges/${id}`,
    body: { game_season_challenge: data }
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/game_season_challenges/${id}`
  } as Endpoint<any>)
}

export type IUseSeasonChallengeTasks = {
  page: number;
  limit: number;
  title?: string;
  game_season_challenge_id?: number;
  action_type?: 'quest' | 'tournament_participation' | 'tournament_win' | 'survey' | 'treasure_play' | 'treasure_win' | 'streak' | 'news_vote' | 'game_rating' | 'referral' | 'badge_claim' | 'shop_purchase';
}

export const AdminSeasonChallengeTasksEndpoints = {
  all: ({ page, limit, title, game_season_challenge_id, action_type }: IUseSeasonChallengeTasks) => {
    let query = `page=${page}&limit=${limit}`
    if (title) query += `&title=${encodeURIComponent(title)}`
    if (game_season_challenge_id) query += `&game_season_challenge_id=${game_season_challenge_id}`
    if (action_type) query += `&action_type=${action_type}`

    return {
      method: Method.GET,
      path: `/admin/game_season_challenge_tasks?${query}`
    } as Endpoint<any>
  },
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/game_season_challenge_tasks/${id}`
  } as Endpoint<any>),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/game_season_challenge_tasks',
    body: { game_season_challenge_task: data }
  } as Endpoint<any>),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/game_season_challenge_tasks/${id}`,
    body: { game_season_challenge_task: data }
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/game_season_challenge_tasks/${id}`
  } as Endpoint<any>)
}

export type IUseGameSeasonClawMachinePrizes = {
  page: number;
  limit: number;
  search?: string;
  active?: boolean;
  reward_type?: 'xp' | 'treasure_chance' | 'claw_coin' | 'token' | 'blockchain_token' | 'store_product' | 'store_discount' | 'physical_item';
  stock_type?: 'limited' | 'unlimited';
}

export const AdminGameSeasonClawMachinePrizesEndpoints = {
  all: ({ page, limit, search, active, reward_type, stock_type, game_season_id }: IUseGameSeasonClawMachinePrizes & { game_season_id: number }) => ({
    method: Method.GET,
    path: `/admin/game_seasons/${game_season_id}/prizes?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}${active !== undefined ? `&active=${active}` : ''}${reward_type ? `&reward_type=${reward_type}` : ''}${stock_type ? `&stock_type=${stock_type}` : ''}`
  } as Endpoint<any>),
  find: ({ id, game_season_id }: { id: string; game_season_id: string }) => ({
    method: Method.GET,
    path: `/admin/game_seasons/${game_season_id}/prizes/${id}`
  } as Endpoint<any>),
  create: ({ data, game_season_id }: { data: any; game_season_id: string }) => ({
    method: Method.POST,
    path: `/admin/game_seasons/${game_season_id}/prizes`,
    body: data
  } as Endpoint<any>),
  update: ({ id, data, game_season_id }: { id: string; data: any; game_season_id: string }) => ({
    method: Method.PUT,
    path: `/admin/game_seasons/${game_season_id}/prizes/${id}`,
    body: data
  } as Endpoint<any>),
  delete: (id: string, game_season_id: string) => ({
    method: Method.DELETE,
    path: `/admin/game_seasons/${game_season_id}/prizes/${id}`
  } as Endpoint<any>)
}

export type IUseClawMachinePrizes = {
  page: number;
  limit: number;
  title?: string;
}

export const AdminClawMachinePrizesEndpoints = {
  all: ({ page, limit, title }: IUseClawMachinePrizes) => ({
    method: Method.GET,
    path: `/admin/claw_machine_prizes?page=${page}&limit=${limit}${title ? `&title=${encodeURIComponent(title)}` : ''}`
  } as Endpoint<any>),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/claw_machine_prizes/${id}`
  } as Endpoint<any>),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/claw_machine_prizes',
    body: { claw_machine_prize: data }
  } as Endpoint<any>),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/claw_machine_prizes/${id}`,
    body: { claw_machine_prize: data }
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/claw_machine_prizes/${id}`
  } as Endpoint<any>)
}

export type IUseGameSeasonClawMachinePrizeClaims = {
  page: number;
  limit: number;
  query?: string | number;
  prizeId: number;
}

export const AdminGameSeasonClawMachinePrizeClaimsEndpoints = {
  all: ({ page, limit, query, prizeId }: IUseGameSeasonClawMachinePrizeClaims) => ({
    method: Method.GET,
    path: `/admin/claw_machine_prize_claims?page=${page}&limit=${limit}${query ? `&query=${encodeURIComponent(query)}` : ''}&prize_id=${prizeId}`
  } as Endpoint<any>)
}

export type IUseClawMachineCoins = {
  page: number;
  limit: number;
  search?: string | number;
  status?: 'used' | 'available' | 'expired';
  user_id?: number;
  quantity?: number;
}

export const AdminClawMachineCoinsEndpoints = {
  all({ page, limit, search, status }: IUseClawMachineCoins) {
    let query = `page=${page}&limit=${limit}`
    if (search) query += `&search=${encodeURIComponent(search)}`
    if (status) query += `&status=${status}`

    return {
      method: Method.GET,
      path: `/admin/claw_machine_coins?${query}`
    }
  },
  create: (data: any) => ({
    method: Method.POST,
    path: `/admin/claw_machine_coins`,
    body: { claw_machine_coin: data }
  }),
  bulk_create: (data: FormData) => ({
    method: Method.POST,
    path: `/admin/claw_machine_coins/bulk_create`,
    body: data,
    isForm: true
  })
}

export type IUseLevelRewards = {
  page: number;
  limit: number;
  level?: number;
  reward_type?: 'xp_bonus' | 'blockchain_token' | 'treasure_opportunity' | 'claw_coin' | 'store_discount' | 'custom_reward';
  active?: boolean;
}

export const AdminLevelRewardsEndpoints = {
  all({ page, limit, level, reward_type, active }: IUseLevelRewards) {
    let query = `page=${page}&limit=${limit}`
    if (level) query += `&level=${level}`
    if (reward_type) query += `&reward_type=${reward_type}`
    if (active !== undefined) query += `&active=${active}`

    return {
      method: Method.GET,
      path: `/admin/level_rewards?${query}`
    }
  },
  create: (data: Partial<AdminLevelReward>) => ({
    method: Method.POST,
    path: `/admin/level_rewards`,
    body: { level_reward: data }
  }),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/level_rewards/${id}`
  }),
  update: (id: string, data: Partial<AdminLevelReward>) => ({
    method: Method.PUT,
    path: `/admin/level_rewards/${id}`,
    body: { level_reward: data }
  }),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/level_rewards/${id}`
  })
}

export type IUseLevelRewardAssignments = {
  page: number;
  limit: number;
  level?: number;
  level_reward_id?: number;
  status?: 'pending' | 'completed' | 'failed';
}

export const AdminLevelRewardAssignmentsEndpoints = {
  all({ page, limit, level, level_reward_id, status }: IUseLevelRewardAssignments) {
    let query = `page=${page}&limit=${limit}`
    if (level) query += `&level=${level}`
    if (level_reward_id) query += `&level_reward_id=${level_reward_id}`
    if (status) query += `&status=${status}`

    return {
      method: Method.GET,
      path: `/admin/level_reward_assignments?${query}`
    }
  },
  retry: (id: string) => ({
    method: Method.POST,
    path: `/admin/level_reward_assignments/${id}/retry`
  })
}

export type IUsePurchaseRewardPromotions = {
  page: number;
  limit: number;
  name?: string;
  active_only?: boolean;
}

export const AdminPurchaseRewardPromotionsEndpoints = {
  all: ({ page, limit, name, active_only }: IUsePurchaseRewardPromotions) => {
    let query = `page=${page}&limit=${limit}`
    if (name) query += `&name=${encodeURIComponent(name)}`
    if (active_only !== undefined) query += `&active_only=${active_only}`

    return {
      method: Method.GET,
      path: `/admin/purchase_reward_promotions?${query}`
    } as Endpoint<any>
  },
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/purchase_reward_promotions/${id}`
  } as Endpoint<any>),
  create: (data: any) => ({
    method: Method.POST,
    path: '/admin/purchase_reward_promotions',
    body: data
  } as Endpoint<any>),
  update: (id: string, data: any) => ({
    method: Method.PUT,
    path: `/admin/purchase_reward_promotions/${id}`,
    body: data
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/purchase_reward_promotions/${id}`
  } as Endpoint<any>)
}

export type IUsePurchaseRewardExclusions = {
  page: number;
  limit: number;
}

export const AdminPurchaseRewardExclusionsEndpoints = {
  all: ({ page, limit }: IUsePurchaseRewardExclusions) => ({
    method: Method.GET,
    path: `/admin/purchase_reward_exclusions?page=${page}&limit=${limit}`
  } as Endpoint<any>),
  create: (data: { product_uid: string; reason?: string }) => ({
    method: Method.POST,
    path: '/admin/purchase_reward_exclusions',
    body: data
  } as Endpoint<any>),
  delete: (id: number) => ({
    method: Method.DELETE,
    path: `/admin/purchase_reward_exclusions/${id}`
  } as Endpoint<any>)
}

// Trivia v2 Endpoints - Templates (Catálogo de Preguntas)
export type IUseTriviaTemplates = {
  page?: number;
  per_page?: number;
  search?: string;
}

export type ICreateTriviaTemplate = {
  title: string;
  text: string;
  explanation?: string;
  points: number;
  time_limit_seconds?: number;
  speed_bonus_enabled?: boolean;
  speed_bonus_multiplier?: number;
  extra_data?: Record<string, any>;
  trivia_template_answers_attributes: Array<{
    text: string;
    is_correct: boolean;
    position: number;
    extra_data?: Record<string, any>;
  }>;
}

export type IUpdateTriviaTemplate = Partial<ICreateTriviaTemplate>

export const AdminTriviaTemplatesEndpoints = {
  all: (filter: IUseTriviaTemplates = {}) => ({
    method: Method.GET,
    path: `/admin/trivia_templates?${parameterize(filter)}`
  } as Endpoint<any>),
  find: (id: string) => ({
    method: Method.GET,
    path: `/admin/trivia_templates/${id}`
  } as Endpoint<any>),
  create: (data: { template: ICreateTriviaTemplate }) => ({
    method: Method.POST,
    path: '/admin/trivia_templates',
    body: data
  } as Endpoint<any>),
  update: (id: string, data: { template: IUpdateTriviaTemplate }) => ({
    method: Method.PUT,
    path: `/admin/trivia_templates/${id}`,
    body: data
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/trivia_templates/${id}`
  } as Endpoint<any>),
  bulkCreate: (data: FormData) => ({
    method: Method.POST,
    path: '/admin/trivia_templates/bulk_create',
    body: data,
    isForm: true
  } as Endpoint<any>)
}

// Trivia v2 Endpoints - Assignments (Calendario)
export type IUseTriviaAssignments = {
  date?: string;
  start_date?: string;
  end_date?: string;
  active?: boolean;
}

export type IUseTriviaCalendar = {
  start_date: string;
  end_date: string;
}

export type ICreateTriviaAssignment = {
  template_id: string;
  date: string;
  position?: number;
  active?: boolean;
}

export type ICreateBulkTriviaAssignments = {
  assignments: Array<{
    template_id: string;
    date: string;
    position?: number;
    active?: boolean;
  }>;
}

export const AdminTriviaAssignmentsEndpoints = {
  all: (filter: IUseTriviaAssignments = {}) => ({
    method: Method.GET,
    path: `/admin/trivia_assignments?${parameterize(filter)}`
  } as Endpoint<any>),
  calendar: (filter: IUseTriviaCalendar) => ({
    method: Method.GET,
    path: `/admin/trivia_assignments/calendar?${parameterize(filter)}`
  } as Endpoint<any>),
  create: (data: { assignment: ICreateTriviaAssignment }) => ({
    method: Method.POST,
    path: '/admin/trivia_assignments',
    body: data
  } as Endpoint<any>),
  bulkCreate: (data: ICreateBulkTriviaAssignments) => ({
    method: Method.POST,
    path: '/admin/trivia_assignments/bulk_create',
    body: data
  } as Endpoint<any>),
  delete: (id: string) => ({
    method: Method.DELETE,
    path: `/admin/trivia_assignments/${id}`
  } as Endpoint<any>)
}

export type IAgentNewsGetAllProps = {
  title?: string;
  status?: 'draft' | 'published';
  ai_generated?: boolean;
  page: number;
  limit: number;
};

export const AdminAgentNewsEndpoints = {
  all: (filter: IAgentNewsGetAllProps) => ({
    method: Method.GET,
    path: `/admin/news?${parameterize(filter)}`,
  } as Endpoint<any>),
  find: (id: number) => ({
    method: Method.GET,
    path: `/admin/news/${id}`,
  } as Endpoint<any>),
  update: (id: number, data: any) => ({
    method: Method.PATCH,
    path: `/admin/news/${id}`,
    body: data,
  } as Endpoint<any>),
  publish: (id: number) => ({
    method: Method.POST,
    path: `/admin/news/${id}/publish`,
  } as Endpoint<any>),
  unpublish: (id: number) => ({
    method: Method.POST,
    path: `/admin/news/${id}/unpublish`,
  } as Endpoint<any>),
}
